import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';

import { I18N } from '@gfe/core/constants/i18n.constants';
import { ParamsService } from '@gfe/core/services/params/params.service';
import { PlanningSpaceService } from '@gfe/core/services/planning-space/planning-space.service';
import { PlansService } from '@gfe/core/services/plans/plans.service';
import { SolverService } from '@gfe/core/services/solvers/solvers.service';
import { selectAvailableEntities } from '@gfe/core/store/dataspace/dataspace.selectors';
import { crumb, namedCrumb } from '@gfe/shared/breadcrumb/breadcrumb.model';

export const HOME_CRUMB = crumb('topbar.breadcrumb.home', { link: false });
export const REPORTING_CRUMB = crumb('sidebar.top.reporting');
export const OPTIMIZATIONS_CRUMB = crumb('sidebar.top.optimizations');
export const DATA_CRUMB = crumb('topbar.breadcrumb.data');
/** A plan's data has no page of its own: its entities are reached from the plan */
export const PLAN_DATA_CRUMB = crumb('topbar.breadcrumb.data', { link: false });
export const UPLOAD_CRUMB = crumb('topbar.breadcrumb.upload');
export const CUSTOM_GEN_CRUMB = crumb('topbar.breadcrumb.custom-gen', { link: false });
export const NEW_CRUMB = crumb('common.new', { link: false });
export const EDIT_CRUMB = crumb('common.edit', { link: false });
export const SOLVERS_CRUMB = crumb('topbar.breadcrumb.solvers');
export const PLANNING_SPACES_CRUMB = crumb('topbar.breadcrumb.planning-spaces');
export const DEPLOY_CRUMB = crumb('topbar.breadcrumb.deploy', { link: false });
export const ADMIN_CRUMB = crumb('topbar.breadcrumb.admin');
export const PROFILE_CRUMB = crumb('topbar.breadcrumb.profile');

/**
 * Names an entity type the way the current dataspace does. Read from the dataspace's own entity
 * list, which follows a dataspace switch — a type only some dataspaces have is still named.
 */
export const ENTITY_TYPE_CRUMB = crumb(({ segment }) =>
  inject(Store)
    .select(selectAvailableEntities)
    .pipe(
      map(
        (entities) =>
          entities.find((entity) => entity.name === segment.toLowerCase())?.displayName
      )
    )
);

export const PLANNING_SPACE_NAME_CRUMB = namedCrumb(
  'planning_space',
  'planningSpaceId',
  (planningSpaceId) =>
    inject(PlanningSpaceService)
      .fetchOne$({ planningSpaceId })
      .pipe(map((space) => space.name)),
  { fallback: (id) => I18N.createFallbackSpaceName(id) }
);

export const PLAN_NAME_CRUMB = namedCrumb(
  'plan',
  'planId',
  (planId) =>
    inject(PlansService)
      .fetchOne$({ planId }, { skipErrorNotification: true })
      .pipe(map((plan) => plan.name)),
  { fallback: (id) => I18N.createFallbackPlanName(id) }
);

export const SOLVER_NAME_CRUMB = namedCrumb(
  'solver',
  'solver',
  (solverId) =>
    inject(SolverService)
      .getSolverById$(Number(solverId))
      .pipe(map((solver) => solver.name)),
  { fallback: (id) => I18N.createFallbackSolverName(id) }
);

export const SOLVER_PARAM_NAME_CRUMB = namedCrumb(
  'solver_param',
  'paramId',
  (paramId, params) =>
    inject(ParamsService)
      .getParamById$(Number(paramId), Number(params['solver']))
      .pipe(map((param) => param.name)),
  { fallback: (id) => I18N.createFallbackParameterName(id) }
);
